import { useState } from "react";
import { MoreHorizontal, Pencil, Trash2, Flag, Loader2, Pin, PinOff, Ban } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useBlockHubUser } from "@/hooks/hub/useHubBlocks";

/**
 * LR HUB 3-dot (kebab) menu for a post or comment — V1 essentials only.
 *
 *  own content  -> Edit (re-moderated by the edge function) + Delete (soft)
 *  other's      -> Report (lands in the admin moderation queue)
 *
 * No save / snooze / hide / unfollow items. Deletes go through the
 * `hub_delete_own_post` / `hub_delete_own_comment` security-definer RPCs, which
 * enforce `author_id = auth.uid()` server-side.
 */

const REPORT_REASONS = [
  "Spam or advertising",
  "Harassment or abusive language",
  "Off-platform solicitation / contact swapping",
  "Misleading or false information",
  "Something else",
];

export function HubItemMenu({
  kind,
  targetId,
  isOwn,
  authorId,
  authorLabel,
  onEdit,
  onDeleted,
  pinned,
  onPinChanged,
}: {
  kind: "post" | "comment";
  targetId: string;
  isOwn: boolean;
  /** Author of this post/comment — enables "Block this user". */
  authorId?: string | null;
  /** Readable Hub handle / display name stored on the block row. */
  authorLabel?: string | null;
  onEdit?: () => void;
  onDeleted?: () => void;
  /** Current pin state (posts only) — drives the admin-only menu entry. */
  pinned?: boolean;
  onPinChanged?: () => void;
}) {
  const { user, isAdmin } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const blockHubUser = useBlockHubUser();

  if (!user?.id) return null;

  const label = kind === "post" ? "post" : "comment";

  async function doDelete() {
    setBusy(true);
    try {
      const { error } = await supabase.rpc(
        kind === "post" ? "hub_delete_own_post" : "hub_delete_own_comment",
        kind === "post" ? { _post_id: targetId } : { _comment_id: targetId },
      );
      if (error) throw error;
      toast.success(`Your ${label} was deleted.`);
      onDeleted?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : `Could not delete this ${label}.`);
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  }

  async function doReport(reason: string) {
    setBusy(true);
    try {
      // Routed through the SAME edge function as /safety (JWT-derived reporter,
      // user_reports insert + admin notification email).
      const { data, error } = await supabase.functions.invoke("submit-safety-report", {
        body: {
          report_type: kind === "post" ? "hub_post" : "hub_comment",
          target_identifier: targetId,
          issue_type: reason,
          details: `LR Hub ${label} reported from the feed. ${label} id: ${targetId}`,
        },
      });
      if (error || (data as { error?: string } | null)?.error) {
        throw new Error((data as { error?: string } | null)?.error || error?.message);
      }
      toast.success("Thanks — our team will review this.");
      setReportOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not send your report.");
    } finally {
      setBusy(false);
    }
  }

  async function doBlock() {
    if (!authorId) return;
    setBusy(true);
    try {
      await blockHubUser(authorId, authorLabel || "LR Hub member");
      toast.success("Blocked. You won't see their Hub content or messages.");
      setBlockOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not block this user.");
    } finally {
      setBusy(false);
    }
  }

  async function doPin(next: boolean) {
    setBusy(true);
    try {
      const { error } = await supabase.rpc("hub_admin_pin_post", {
        _post_id: targetId,
        _pinned: next,
      });
      if (error) throw error;
      toast.success(next ? "Pinned to this feed." : "Unpinned.");
      onPinChanged?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not update the pin.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`More options for this ${label}`}
            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {isOwn ? (
            <>
              <DropdownMenuItem onSelect={() => onEdit?.()} className="text-[12px]">
                <Pencil className="mr-2 h-3.5 w-3.5" /> Edit {label}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setConfirmOpen(true)}
                className={cn("text-[12px] text-danger focus:text-danger")}
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete {label}
              </DropdownMenuItem>
            </>
          ) : (
            <>
              <DropdownMenuItem onSelect={() => setReportOpen(true)} className="text-[12px]">
                <Flag className="mr-2 h-3.5 w-3.5" /> Report {label}
              </DropdownMenuItem>
              {authorId && authorId !== user.id ? (
                <DropdownMenuItem
                  onSelect={() => setBlockOpen(true)}
                  className={cn("text-[12px] text-danger focus:text-danger")}
                >
                  <Ban className="mr-2 h-3.5 w-3.5" /> Block this user
                </DropdownMenuItem>
              ) : null}
            </>
          )}
          {kind === "post" && isAdmin ? (
            <DropdownMenuItem
              disabled={busy}
              onSelect={() => void doPin(!pinned)}
              className="text-[12px]"
            >
              {pinned ? (
                <><PinOff className="mr-2 h-3.5 w-3.5" /> Unpin</>
              ) : (
                <><Pin className="mr-2 h-3.5 w-3.5" /> Pin to feed</>
              )}
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {label}?</AlertDialogTitle>
            <AlertDialogDescription>
              It will be removed from the LR Hub feed. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                void doDelete();
              }}
            >
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={blockOpen} onOpenChange={setBlockOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Block {authorLabel || "this member"}?</AlertDialogTitle>
            <AlertDialogDescription>
              You won't see their posts or comments in the LR Hub, and direct messages
              between you will be unavailable. You can undo this from Safety &amp; Reporting.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                void doBlock();
              }}
            >
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              Block
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Report this {label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            {REPORT_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                disabled={busy}
                onClick={() => void doReport(r)}
                className="w-full rounded-lg border border-border px-3 py-2 text-left text-[12px] font-medium transition-colors hover:bg-muted disabled:opacity-60"
              >
                {r}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default HubItemMenu;
